import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';

const estilos = {
  adequado: { borderColor: 'success.main', bgcolor: '#f2fbf5', icon: 'success.main' },
  subdimensionado: { borderColor: 'error.main', bgcolor: '#fff5f5', icon: 'error.main' },
  sem_kit: { borderColor: 'error.main', bgcolor: '#fff5f5', icon: 'error.main' },
  sobredimensionado: { borderColor: 'warning.main', bgcolor: '#fff8e1', icon: 'warning.main' },
};

const sinalPercentual = (valor) => {
  if (valor === null || valor === undefined || !Number.isFinite(Number(valor))) return '—';
  const n = Number(valor);
  return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
};

const descricaoNivel = (nivel) => {
  if (nivel === 0) return 'Mesma configuração';
  if (nivel === 1) return 'Mesmo fornecedor / outro módulo';
  if (nivel === 2) return 'Outro fornecedor / mesmo módulo';
  return 'Outra configuração';
};

export default function AlertaAdequacaoKit({
  adequacao,
  alternativas = [],
  onCadastrar,
  onUsarAlternativa,
  disabled = false,
}) {
  const [mostrarAlternativas, setMostrarAlternativas] = useState(false);

  const alternativasUnicas = useMemo(() => {
    const mapa = new Map();
    (alternativas || []).forEach((kit) => {
      if (kit?._id) mapa.set(String(kit._id), kit);
    });
    return [...mapa.values()].slice(0, 8);
  }, [alternativas]);

  if (!adequacao || adequacao.status === 'indefinido') return null;

  const sx = estilos[adequacao.status] || estilos.sobredimensionado;
  const obrigatorio = Boolean(adequacao.bloqueia);
  const Icone = adequacao.status === 'adequado' ? CheckCircleOutlineIcon : WarningAmberIcon;

  const resumoPotencia = adequacao.potenciaKit
    ? `Necessária: ${Number(adequacao.potenciaNecessaria || 0).toFixed(2)} kWp • Kit: ${Number(adequacao.potenciaKit).toFixed(2)} kWp • Diferença: ${sinalPercentual(adequacao.percentual)}`
    : `Potência necessária: ${Number(adequacao.potenciaNecessaria || 0).toFixed(2)} kWp`;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderColor: sx.borderColor, bgcolor: sx.bgcolor }}>
      <Stack spacing={1.25}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Icone fontSize="small" sx={{ color: sx.icon, mt: '2px' }} />
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>{adequacao.titulo}</Typography>
              <Typography variant="body2" fontWeight={600}>{resumoPotencia}</Typography>
              {adequacao.mensagem && (
                <Typography variant="body2" color="text.secondary">{adequacao.mensagem}</Typography>
              )}
              {obrigatorio && alternativasUnicas.length === 0 && (
                <Typography variant="caption" color="error.main" fontWeight={700}>
                  Não há alternativa compatível no catálogo atual. É necessário cadastrar um novo kit para avançar.
                </Typography>
              )}
              {obrigatorio && alternativasUnicas.length > 0 && (
                <Typography variant="caption" color="warning.dark" fontWeight={700}>
                  Encontramos {alternativasUnicas.length} alternativa(s) compatível(is) em outro fornecedor ou modelo.
                  Escolha uma alternativa ou cadastre um novo kit.
                </Typography>
              )}
            </Box>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignSelf={{ xs: 'stretch', md: 'center' }}>
            {alternativasUnicas.length > 0 && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<CompareArrowsIcon />}
                onClick={() => setMostrarAlternativas((v) => !v)}
                disabled={disabled}
              >
                {mostrarAlternativas ? 'Ocultar alternativas' : `Ver alternativas (${alternativasUnicas.length})`}
              </Button>
            )}
            {(adequacao.status !== 'adequado' || alternativasUnicas.length === 0) && (
              <Button
                variant={obrigatorio && alternativasUnicas.length === 0 ? 'contained' : 'outlined'}
                color={obrigatorio && alternativasUnicas.length === 0 ? 'error' : 'warning'}
                startIcon={<AddCircleOutlineIcon />}
                onClick={onCadastrar}
                disabled={disabled}
              >
                {adequacao.status === 'sobredimensionado' ? 'Cadastrar kit mais adequado' : 'Cadastrar kit'}
              </Button>
            )}
          </Stack>
        </Stack>

        <Collapse in={mostrarAlternativas}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fornecedor</TableCell>
                  <TableCell>Módulo</TableCell>
                  <TableCell align="right">Potência</TableCell>
                  <TableCell align="right">Diferença</TableCell>
                  <TableCell>Situação</TableCell>
                  <TableCell>Compatibilidade</TableCell>
                  <TableCell>Origem</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {alternativasUnicas.map((kit) => {
                  const ad = kit._adequacao || {};
                  return (
                    <TableRow key={kit._id}>
                      <TableCell>{kit.fornecedor?.nome || '—'}</TableCell>
                      <TableCell>
                        {[kit.modulo?.marca, kit.modulo?.modelo, kit.modulo?.potencia ? `${kit.modulo.potencia} W` : '']
                          .filter(Boolean).join(' ') || '—'}
                      </TableCell>
                      <TableCell align="right">{Number(kit.potenciacc || 0).toFixed(2)} kWp</TableCell>
                      <TableCell align="right">
                        <strong>{sinalPercentual(ad.percentual)}</strong>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={ad.status === 'sobredimensionado' ? 'warning' : 'success'}
                          label={ad.status === 'sobredimensionado' ? 'Sobredimensionado' : 'Adequado'}
                        />
                      </TableCell>
                      <TableCell>
                        {Number.isFinite(Number(kit._autonomiaLiquida))
                          ? `Autonomia útil ${Number(kit._autonomiaLiquida).toFixed(2)} kWh`
                          : Number.isFinite(Number(kit._potenciaInversorCV))
                            ? `Inversor ${Number(kit._potenciaInversorCV).toFixed(1)} CV`
                            : 'Compatível'}
                      </TableCell>
                      <TableCell>{descricaoNivel(kit._nivelAlternativa)}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => onUsarAlternativa?.(kit)}
                          disabled={disabled}
                        >
                          Usar este kit
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      </Stack>
    </Paper>
  );
}
